import { useCallback, useRef } from 'react';
import { log } from 'log';
import { useTranslations } from 'js/hooks/i18n';
import { useGetErrorMessage } from 'js/hooks/server';
import { useCheckUsernameAvailability } from 'server';
import { TValidationMethod } from 'js/utils/Validation/types';
import { ICheckUsernameAvailabilityResponseData } from 'js/hooks/socket/hooks/useCheckUsernameAvailability/types';

import * as Types from './types';
import { INTERNAL_EMAIL } from '../../../../constants';
import { useWithRecordLevelValidation } from '../useWithRecordLevelValidation';

export const useValidateEmail = ({
    fieldName,
    requestReCaptchaToken,
    abortReCaptchaTokenRequest
}: Types.IUseValidateEmailOptions) => {
    const translate = useTranslations();
    const getErrorMessage = useGetErrorMessage();
    const checkIfEmailIsAvailable = useCheckUsernameAvailability();
    const cachedEmail = useRef<string | null>(null);
    const abortController = useRef<AbortController | null>(null);
    const cachedRecaptchaValidationRequest = useRef<Promise<{ token: string }> | null>(null);
    const cachedServerResponse = useRef<ICheckUsernameAvailabilityResponseData | null>(null);
    const withRecordLevelValidation = useWithRecordLevelValidation({ fieldName });

    // this has to be async function so that React Final Form will wait for resolution
    const asyncServerRequest = useCallback(async function process(email: string, validationToken?: string):
        Promise<Types.IUseValidateEmailResponse> {
        try {
            const response = await checkIfEmailIsAvailable({
                email,
                ...validationToken && { token: validationToken }
            });

            const { emailAvailable } = response;

            cachedEmail.current = email;
            cachedServerResponse.current = response;
            cachedRecaptchaValidationRequest.current = null;
            abortController.current?.abort();

            if (emailAvailable === 'unavailable') {
                return translate('playtech.mobile.system.form.email.internal.emailIsInUse');
            }
        } catch (error: Types.TCheckIfEmailIsAvailableErrorResponse) {
            const { data: { errorCode } } = error.response;

            if (errorCode === 301) {
                try {
                    abortController.current = new AbortController();
                    abortController.current?.signal.addEventListener('abort', abortReCaptchaTokenRequest);

                    const tokenResponse = requestReCaptchaToken();

                    cachedRecaptchaValidationRequest.current = tokenResponse;

                    const { token } = await tokenResponse;

                    return await process(email, token);
                } catch (validationError) {
                    log('error', validationError);

                    return getErrorMessage(validationError);
                }
            }

            return getErrorMessage(error);
        }
    }, [
        translate,
        getErrorMessage,
        requestReCaptchaToken,
        checkIfEmailIsAvailable,
        abortReCaptchaTokenRequest
    ]);

    // this has to be async function so that React Final Form will wait for resolution
    const asyncCachedRequest = useCallback(
        async (email: string, recaptchaValidationPromise: Promise<{ token: string }>) => {
            let recaptchaToken;

            try {
                const data = await recaptchaValidationPromise;

                recaptchaToken = data.token;
            } catch {
                return await asyncServerRequest(email);
            }

            return await asyncServerRequest(email, recaptchaToken);
        }, [asyncServerRequest]);

    const emailValidate = useCallback<TValidationMethod<string>>(async (email) => {
        if (!email || email === INTERNAL_EMAIL) {
            return;
        }

        if (cachedEmail.current === email) {
            const { emailAvailable } = cachedServerResponse.current ?? {};

            if (emailAvailable !== 'unavailable') {
                return;
            }

            return translate('playtech.mobile.system.form.email.internal.emailIsInUse');
        }

        if (cachedRecaptchaValidationRequest.current) {
            return asyncCachedRequest(email, cachedRecaptchaValidationRequest.current);
        }

        return asyncServerRequest(email);
    }, [
        translate,
        asyncServerRequest,
        asyncCachedRequest
    ]);

    return withRecordLevelValidation(emailValidate);
};
